import type { CallStatus } from "@/pages/Chats/CallModal";
import { inCallLabel } from "@/pages/Chats/callHold";

// Исходящий звонок на весь шелл — чистая часть, без React и без софтфона: что
// открыто в окне звонка, свёрнуто ли оно и кому сказать, чем всё кончилось.
// Хост (OutgoingCallHost) кормит её действиями и исполняет то, что она
// вернула, поэтому всё поведение проверяется тестом.
//
// Окно одно на вкладку, как и звонок (SIP-пользователь у всех один, см. реестр
// в softphone.ts). Отсюда правила:
//   • пока линия жива, второй open() ничего не начинает — он разворачивает
//     окно идущего звонка (и говорит, чей он, если звонить хотели другому);
//   • без живой линии open() заменяет прежнее окно — итог «завершён» или
//     ошибку прошлого звонка; тому, кто его открывал, при этом сообщают;
//   • свернуть можно только живой звонок: до набора окно — обычный диалог;
//   • открывший окно может исчезнуть раньше звонка (ушли со страницы, закрыли
//     карточку) — звонок от этого не зависит, просто сказать потом некому.

/** Чем кончилось окно звонка — тому, кто его открывал. */
export interface OutgoingCallResult {
  // Разговор состоялся (линия побывала в «Разговор»), а не просто набирали.
  talked: boolean;
}

/** Заявка на звонок — то, что страница передаёт в useOutgoingCall().open(). */
export interface OutgoingCallRequest {
  personName: string;
  phones: string[];
  /** Этот номер в окне первый («Перезвонить» с плашки пропущенного), даже
   *  если у человека их несколько. */
  number?: string | null;
  /** Кандидатура собеседника — для «Карточка кандидата» в окне звонка. */
  candidateId?: string | null;
  /** Звоним прежнему руководителю (ЛПР) — его контакт. */
  referenceId?: string | null;
  /** Окно закрылось. Не зовётся, если открывший к этому времени размонтирован
   *  и если звонок так и не открылся (вкладка была занята другим). */
  onClosed?: (r: OutgoingCallResult) => void;
}

export interface OutgoingSession {
  // Номер окна. Новое окно — новый ключ React: выбор номера и устройств в нём
  // начинается с чистого листа.
  id: number;
  personName: string;
  // Номера в том порядке, в каком их покажет окно.
  phones: string[];
  candidateId: string | null;
  referenceId: string | null;
  // Кто открыл (номер экземпляра useOutgoingCall) и что ему сказать, когда
  // окно закроется. null — говорить некому: открывший размонтирован.
  opener: number;
  onClosed: ((r: OutgoingCallResult) => void) | null;
  talked: boolean;
}

export interface OutgoingCallState {
  session: OutgoingSession | null;
  minimized: boolean;
  // Что хост знает о линии своего софтфона.
  line: CallStatus["state"];
  // «Сначала завершите текущий звонок: …» — в окне, после второго open().
  notice: string | null;
  // Сколько окон уже открывали (номер последнего).
  seq: number;
}

export const initialOutgoingCall: OutgoingCallState = {
  session: null,
  minimized: false,
  line: "idle",
  notice: null,
  seq: 0,
};

export type OutgoingAction =
  | { type: "open"; req: OutgoingCallRequest; opener: number }
  // Состояние софтфона сменилось.
  | { type: "line"; line: CallStatus["state"] }
  | { type: "minimize" }
  | { type: "expand" }
  // Окно закрыли: крестик до набора, итог показан, «Закрыть» на плашке.
  | { type: "close" }
  // Экземпляр useOutgoingCall размонтирован.
  | { type: "opener_gone"; opener: number };

/** Кому и что сказать о закрывшемся окне. */
export interface ClosedCall {
  onClosed: (r: OutgoingCallResult) => void;
  talked: boolean;
}

export interface OutgoingStep {
  state: OutgoingCallState;
  // null — говорить некому (или окно не закрывалось).
  closed: ClosedCall | null;
}

/** Линия жива: от «Соединение…» до конца разговора. */
export function lineLive(line: CallStatus["state"]): boolean {
  return line === "connecting" || line === "ringing" || line === "listening" || line === "in_call";
}

function digits(p: string): string {
  return p.replace(/\D/g, "");
}

/** Номера для окна: `number` — первым. Его может и не быть среди номеров
 * человека (звонил с незнакомого) — тогда он просто добавляется. */
export function orderPhones(phones: string[], number?: string | null): string[] {
  if (!number) return phones;
  const d = digits(number);
  return [number, ...phones.filter((p) => (d ? digits(p) !== d : p !== number))];
}

/** Заявка — тому же собеседнику, что и открытое окно. ЛПР — по контакту,
 * остальные — по номеру: кандидату звонят и из ленты, и из карточки. */
export function sameCallee(
  s: Pick<OutgoingSession, "personName" | "phones" | "referenceId">,
  req: Pick<OutgoingCallRequest, "personName" | "phones" | "number" | "referenceId">,
): boolean {
  const ref = req.referenceId ?? null;
  if (s.referenceId || ref) return s.referenceId === ref;
  const have = new Set(s.phones.map(digits).filter(Boolean));
  const want = orderPhones(req.phones, req.number).map(digits).filter(Boolean);
  if (!have.size && !want.length) return s.personName === req.personName;
  return want.some((p) => have.has(p));
}

export function busyNotice(personName: string): string {
  return `Сначала завершите текущий звонок: ${personName}`;
}

/** Какую кандидатуру откроет «Карточка кандидата» в окне звонка.
 *
 * Звонят из ленты внутри открытой карточки (её чат; `shown` — карточка из
 * адреса) — окно возвращает к ней же. Своя догадка ленты (`latest`) — самая
 * свежая кандидатура человека, а их бывает несколько: ссылка на неё подменила
 * бы карточку, в которой рекрутёр работает, и закрыла бы в ней чат. С открытой
 * карточкой окно просто сворачивается (CallModal, openCard). */
export function callCardId(shown: string | null | undefined, latest: string | null | undefined): string | null {
  return shown || latest || null;
}

function closedOf(s: OutgoingSession | null): ClosedCall | null {
  return s?.onClosed ? { onClosed: s.onClosed, talked: s.talked } : null;
}

const keep = (state: OutgoingCallState): OutgoingStep => ({ state, closed: null });

export function stepOutgoing(state: OutgoingCallState, action: OutgoingAction): OutgoingStep {
  const s = state.session;
  switch (action.type) {
    case "open": {
      const { req } = action;
      // Звонок уже идёт: второй не начинаем, показываем этот. Заявка при этом
      // пропадает целиком — её onClosed не позовут.
      if (s && lineLive(state.line)) {
        const notice = sameCallee(s, req) ? null : busyNotice(s.personName);
        if (!state.minimized && notice === state.notice) return keep(state);
        return keep({ ...state, minimized: false, notice });
      }
      const id = state.seq + 1;
      return {
        state: {
          session: {
            id,
            personName: req.personName,
            phones: orderPhones(req.phones, req.number),
            candidateId: req.candidateId ?? null,
            referenceId: req.referenceId ?? null,
            opener: action.opener,
            onClosed: req.onClosed ?? null,
            talked: false,
          },
          minimized: false,
          // Хост сбрасывает софтфон: хвост прошлого звонка новому окну не нужен.
          line: "idle",
          notice: null,
          seq: id,
        },
        // Прежнее окно (итог или ошибка прошлого звонка) этим и закрылось.
        closed: closedOf(s),
      };
    }
    case "line": {
      const { line } = action;
      if (!s) return state.line === line ? keep(state) : keep({ ...state, line });
      // Трубку положили до ответа («Завершить» на плашке): в развёрнутом окне
      // отсюда набирают заново, а плашке показывать больше нечего.
      if (line === "idle" && state.minimized) {
        return {
          state: { ...state, session: null, minimized: false, line, notice: null },
          closed: closedOf(s),
        };
      }
      // Факт разговора запоминается, пока он идёт: рекрутёр обычно кладёт
      // трубку сам, и состояние в момент закрытия о разговоре уже не говорит.
      const talked = s.talked || line === "in_call";
      // «Сначала завершите…» — только пока есть что завершать.
      const notice = lineLive(line) ? state.notice : null;
      if (line === state.line && talked === s.talked && notice === state.notice) return keep(state);
      return keep({ ...state, line, notice, session: talked === s.talked ? s : { ...s, talked } });
    }
    case "minimize":
      // До набора (и после него) окно — обычный диалог: его закрывают.
      if (!s || state.minimized || !lineLive(state.line)) return keep(state);
      return keep({ ...state, minimized: true, notice: null });
    case "expand":
      if (!s || !state.minimized) return keep(state);
      return keep({ ...state, minimized: false });
    case "close":
      if (!s) return keep(state);
      return {
        state: { ...state, session: null, minimized: false, line: "idle", notice: null },
        closed: closedOf(s),
      };
    case "opener_gone":
      // Звонок остаётся — он и должен пережить страницу, с которой начат.
      if (!s || s.opener !== action.opener || !s.onClosed) return keep(state);
      return keep({ ...state, session: { ...s, onClosed: null } });
  }
}

// ---- окно и плашка ----

/** Что делает «Назад» телефона с развёрнутым окном: звонок идёт — окно
 * сворачивается в плашку (звонок продолжается), иначе — закрывается. */
export function windowBack(line: CallStatus["state"]): "minimize" | "close" {
  return lineLive(line) ? "minimize" : "close";
}

/** Кнопки свёрнутой плашки: «Завершить» у живого звонка, «Закрыть» у
 * ошибки — она висит, пока её не увидят. Итог («завершён») уходит сам. */
export function miniAction(line: CallStatus["state"]): "hang" | "dismiss" | null {
  if (lineLive(line)) return "hang";
  return line === "error" ? "dismiss" : null;
}

// Цвета строки состояния плашки — .icall-mini-status--* (inboundCall.css).
export type MiniTone = "wait" | "live" | "err" | "done";

function clock(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

/** Строка состояния свёрнутой плашки. `flags` — «На удержании» и «Без звука»
 * рекрутёра: они видны и на свёрнутом звонке. */
export function miniStatus(
  status: CallStatus,
  now: number,
  flags: { held?: boolean; muted?: boolean } = {},
): { text: string; tone: MiniTone } {
  switch (status.state) {
    case "connecting":
    // Прослушка — у входящего; исходящий сюда не попадает.
    case "listening":
      return { text: "Соединение…", tone: "wait" };
    case "ringing":
      return { text: "Вызов…", tone: "wait" };
    case "in_call":
      return {
        text: inCallLabel(clock((now - status.since) / 1000), flags),
        tone: flags.held ? "wait" : "live",
      };
    case "ended":
      return {
        text: status.duration_s ? `Завершён · ${clock(status.duration_s)}` : "Завершён",
        tone: "done",
      };
    case "error":
      return { text: status.message, tone: "err" };
    // Трубку положили до ответа: плашка закроется следующим же шагом хоста.
    case "idle":
      return { text: "Завершён", tone: "done" };
  }
}
